import { useCallback, useEffect, useState } from 'react';
import { RecoveryFlow, UpdateRecoveryFlowBody, UiNodeInputAttributes } from '@ory/client';
import { useSearchParams } from 'react-router-dom';
import { isUiNodeInputAttributes } from '@ory/integrations/ui';
import { ory } from 'src/providers/authProvider';
import { Logger } from 'src/utils/Logger';
import { isOryApiError, getCsrfTokenFromFlow } from 'src/auth/utils/ory-helpers';

type RecoveryStep = 'email' | 'code';

interface UseRecoveryFlowReturn {
  flow: RecoveryFlow | undefined;
  isLoading: boolean;
  error: string | null;
  step: RecoveryStep;
  submittedEmail: string;
  csrfToken: string;
  defaultEmail: string;
  submitEmail: (email: string) => Promise<{ success: boolean }>;
  submitCode: (code: string) => Promise<{ success: boolean }>;
  clearError: () => void;
  goBackToEmail: () => void;
}

export const useRecoveryFlow = (): UseRecoveryFlowReturn => {
  const [searchParams] = useSearchParams();
  const [flow, setFlow] = useState<RecoveryFlow>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<RecoveryStep>('email');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const flowIdParam = searchParams.get('flow');
  const emailParam = searchParams.get('email');

  useEffect(() => {
    let cancelled = false;

    const initFlow = async () => {
      try {
        if (flowIdParam) {
          const { data } = await ory.getRecoveryFlow({ id: flowIdParam });
          if (!cancelled) {
            setFlow(data);
            // If flow is already in code verification state, show code input
            if (data.state === 'sent_email') {
              setStep('code');
            }
          }
        } else {
          const { data } = await ory.createBrowserRecoveryFlow();
          if (!cancelled) setFlow(data);
        }
      } catch (err: unknown) {
        if (cancelled) return;
        Logger.error('Failed to initialize recovery flow', err);

        if (isOryApiError(err) && err.response?.status === 410) {
          setError('This recovery link has expired. Please request a new one.');
        } else {
          setError('Failed to initialize. Please try refreshing the page.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    initFlow();

    return () => {
      cancelled = true;
    };
  }, [flowIdParam]);

  const csrfToken = getCsrfTokenFromFlow(flow);

  const defaultEmail = emailParam ?? '';

  const extractErrors = (responseData: RecoveryFlow): string[] => {
    const uiMessages = responseData.ui.messages ?? [];
    const uiErrors = uiMessages.filter(m => m.type === 'error');

    const nodeErrors =
      responseData.ui.nodes?.flatMap(node => node.messages ?? []).filter(m => m.type === 'error') ??
      [];

    return [...uiErrors, ...nodeErrors].map(m => m.text);
  };

  const submitEmail = useCallback(
    async (email: string): Promise<{ success: boolean }> => {
      if (!flow) {
        setError('Recovery flow not initialized. Please refresh the page.');
        return { success: false };
      }

      setError(null);

      try {
        const body = {
          method: 'code',
          email,
          recovery_address: email,
          csrf_token: csrfToken,
        } as UpdateRecoveryFlowBody;
        const { data } = await ory.updateRecoveryFlow({
          flow: flow.id,
          updateRecoveryFlowBody: body,
        });

        Logger.info('Recovery flow state after email submit:', data.state);
        setFlow(data);

        const hasCodeNode = data.ui.nodes.some(
          node =>
            isUiNodeInputAttributes(node.attributes) &&
            (node.attributes as UiNodeInputAttributes).name === 'code',
        );

        if (data.state === 'sent_email' || data.state === 'choose_method' || hasCodeNode) {
          setSubmittedEmail(email);
          setStep('code');
          return { success: true };
        }

        const errors = extractErrors(data);
        if (errors.length > 0) {
          setError(errors.join(' '));
        } else {
          Logger.warn('Unexpected recovery flow state after email submit:', data.state);
          setError('Unable to send recovery code. Please try again.');
        }
        return { success: false };
      } catch (err: unknown) {
        Logger.error('Failed to submit recovery email', err);

        if (!isOryApiError(err)) {
          setError('An unexpected error occurred. Please try again.');
          return { success: false };
        }

        const responseData = err.response?.data as RecoveryFlow | undefined;
        if (responseData?.ui) {
          setFlow(responseData);
          const errors = extractErrors(responseData);
          if (errors.length > 0) {
            setError(errors.join(' '));
          } else {
            setError('Failed to send recovery code. Please try again.');
          }
        } else if (err.response?.status === 400) {
          setError('Invalid request. Please check your email and try again.');
        } else if (err.response?.status === 410) {
          setError('This recovery session has expired. Please refresh the page and try again.');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        return { success: false };
      }
    },
    [flow, csrfToken],
  );

  const submitCode = useCallback(
    async (code: string): Promise<{ success: boolean }> => {
      if (!flow) {
        setError('Recovery flow not initialized. Please refresh the page.');
        return { success: false };
      }

      setError(null);

      try {
        const body = {
          method: 'code',
          code,
          csrf_token: csrfToken,
        } as UpdateRecoveryFlowBody;
        const { data } = await ory.updateRecoveryFlow({
          flow: flow.id,
          updateRecoveryFlowBody: body,
        });

        setFlow(data);

        // If successful, Ory redirects to settings flow to set new password
        // The redirect is handled automatically by Ory's browser flow
        if (data.state === 'passed_challenge') {
          return { success: true };
        }

        const errors = extractErrors(data);
        if (errors.length > 0) {
          setError(errors.join(' '));
        }
        return { success: false };
      } catch (err: unknown) {
        Logger.error('Failed to submit recovery code', err);

        if (!isOryApiError(err)) {
          setError('An unexpected error occurred. Please try again.');
          return { success: false };
        }

        const responseData = err.response?.data as RecoveryFlow | undefined;
        if (responseData?.ui) {
          setFlow(responseData);
          const errors = extractErrors(responseData);
          if (errors.length > 0) {
            setError(errors.join(' '));
          } else {
            setError('Invalid code. Please try again.');
          }
        } else if (err.response?.status === 400) {
          setError('Invalid code. Please check and try again.');
        } else if (err.response?.status === 410) {
          setError('This code has expired. Please request a new one.');
          setStep('email');
        } else {
          setError('An unexpected error occurred. Please try again.');
        }
        return { success: false };
      }
    },
    [flow, csrfToken],
  );

  const clearError = useCallback(() => setError(null), []);

  const goBackToEmail = useCallback(() => {
    setStep('email');
    setError(null);
  }, []);

  return {
    flow,
    isLoading,
    error,
    step,
    submittedEmail,
    csrfToken,
    defaultEmail,
    submitEmail,
    submitCode,
    clearError,
    goBackToEmail,
  };
};
